import { validateMonitorSearch } from '@api-monitoring/features/request-log-filters';
import {
  validateRolesSearch,
  validateTeamsSearch,
  validateUsersSearch,
} from '@auth/features/user-management-filters';
import { validatePricingTypeSearch } from '@products/features/pricing-type-filters';
import { validateVatSearch } from '@products/features/vat-filters';
import {
  createRoute,
  lazyRouteComponent,
  Outlet,
} from '@tanstack/react-router';
import {
  validateCollectionsSearch,
  validateTasksManagementSearch,
} from '@task-manager/features/tasks-filters';
import { protectedRoute, redirect } from './_root';

// ─── Tasks Management ────────────────────────────────────────────

export const tasksManagementRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'tasks-management',
  component: lazyRouteComponent(() =>
    import('@task-manager/pages/tasks-management').then((m) => ({
      default: m.TasksManagementPage,
    }))
  ),
});

export const tasksManagementIndexRoute = createRoute({
  getParentRoute: () => tasksManagementRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/tasks-management/tasks' });
  },
});

export const tasksManagementTasksRoute = createRoute({
  getParentRoute: () => tasksManagementRoute,
  path: 'tasks',
  component: lazyRouteComponent(() =>
    import('@task-manager/pages/tasks-management').then((m) => ({
      default: m.TasksTab,
    }))
  ),
  validateSearch: (input: Record<string, unknown>) =>
    validateTasksManagementSearch(input),
});

export const tasksManagementCollectionsRoute = createRoute({
  getParentRoute: () => tasksManagementRoute,
  path: 'collections',
  component: lazyRouteComponent(() =>
    import('@task-manager/pages/tasks-management').then((m) => ({
      default: m.CollectionsTab,
    }))
  ),
  validateSearch: (input: Record<string, unknown>) =>
    validateCollectionsSearch(input),
});

// ─── User Settings (admin) ───────────────────────────────────────

export const userSettingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'user-settings',
  component: Outlet,
});

export const userSettingsIndexRoute = createRoute({
  getParentRoute: () => userSettingsRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/user-settings/accounts' });
  },
});

export const userSettingsAccountsRoute = createRoute({
  getParentRoute: () => userSettingsRoute,
  path: 'accounts',
  component: lazyRouteComponent(() =>
    import('@auth/pages/user-management-accounts').then((m) => ({
      default: m.UserManagementAccountsPage,
    }))
  ),
  validateSearch: (input: Record<string, unknown>) =>
    validateUsersSearch(input),
});

export const userSettingsTeamsRoute = createRoute({
  getParentRoute: () => userSettingsRoute,
  path: 'teams',
  component: lazyRouteComponent(() =>
    import('@auth/pages/user-management-teams').then((m) => ({
      default: m.UserManagementTeamsPage,
    }))
  ),
  validateSearch: (input: Record<string, unknown>) =>
    validateTeamsSearch(input),
});

export const userSettingsRolesRoute = createRoute({
  getParentRoute: () => userSettingsRoute,
  path: 'roles',
  component: lazyRouteComponent(() =>
    import('@auth/pages/user-management-roles').then((m) => ({
      default: m.UserManagementRolesPage,
    }))
  ),
  validateSearch: (input: Record<string, unknown>) =>
    validateRolesSearch(input),
});

// ─── System Configuration (admin) ─────────────────────────────────

export const systemConfigRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'system-configuration',
  component: Outlet,
});

export const systemConfigIndexRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/system-configuration/organization' });
  },
});

// XT-708 phase 3 — status flow is its own destination. The three tabs below
// used to live under /system-configuration; the old paths still resolve (they
// redirect) so bookmarks and any link written before the move keep working.
// `Outlet`, not the layout: each page wraps itself in `StatusFlowLayout` (the
// same arrangement `systemConfigRoute` uses), so putting it here too would
// render the whole shell twice.
export const statusFlowRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'status-flow',
  component: Outlet,
});

export const statusFlowIndexRoute = createRoute({
  getParentRoute: () => statusFlowRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/status-flow/itinerary' });
  },
});

// XT-708 — the itinerary status process. Lives here rather than under
// /itineraries because it configures the organization, not an itinerary, and
// the surface is admin-gated by the layout.
export const statusFlowItineraryRoute = createRoute({
  getParentRoute: () => statusFlowRoute,
  path: 'itinerary',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/itinerary-status-design').then((m) => ({
      default: m.ItineraryStatusDesignPage,
    }))
  ),
});

// XT-708 phase 3 — the tab ships before the feature, on purpose. See the page.
export const statusFlowOperationsRoute = createRoute({
  getParentRoute: () => statusFlowRoute,
  path: 'operations',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/operation-status-flow').then((m) => ({
      default: m.OperationStatusFlowPage,
    }))
  ),
});

// The status CATALOG briefly lived here beside the flows that arrange it.
// XT-773 sent it back to System Configuration — this page is super-user-only
// now, and an org must still be able to name its own operation statuses. Kept
// as a redirect so the address the tab answered to keeps working.
export const statusFlowOperationsCatalogRoute = createRoute({
  getParentRoute: () => statusFlowRoute,
  path: 'operation-statuses',
  beforeLoad: () => {
    throw redirect({ to: '/system-configuration/operations-status' });
  },
});

// XT-798 — the action library and its trace.
export const statusFlowActionsRoute = createRoute({
  getParentRoute: () => statusFlowRoute,
  path: 'actions',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/status-actions').then((m) => ({
      default: m.StatusActionsPage,
    }))
  ),
});

export const statusFlowActionLogsRoute = createRoute({
  getParentRoute: () => statusFlowRoute,
  path: 'action-logs',
  component: lazyRouteComponent(() =>
    import('@itineraries/pages/status-action-logs').then((m) => ({
      default: m.StatusActionLogsPage,
    }))
  ),
});

// The paths these tabs answered to before the move. Kept as redirects rather
// than deleted: the Configuration menu, the docs and anyone's bookmarks all
// pointed at them.
export const systemConfigStatusProcessRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'status-process',
  beforeLoad: () => {
    throw redirect({ to: '/status-flow/itinerary' });
  },
});

export const systemConfigStatusActionsRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'status-actions',
  beforeLoad: () => {
    throw redirect({ to: '/status-flow/actions' });
  },
});

export const systemConfigStatusActionLogsRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'status-action-logs',
  beforeLoad: () => {
    throw redirect({ to: '/status-flow/action-logs' });
  },
});

export const systemConfigOrganizationRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'organization',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-organization').then((m) => ({
      default: m.SystemConfigOrganizationPage,
    }))
  ),
});

// Integrations — everything connecting this org to something outside it
// (resource sharing, Optigest, Product API, Travefy). Split out of the
// Organization tab, which had grown to hold branding, colours, contact fields
// and four unrelated integration cards.
export const systemConfigIntegrationsRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'integrations',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-integrations').then((m) => ({
      default: m.SystemConfigIntegrationsPage,
    }))
  ),
});

// XT-782 — Preview follow-up: the "your traveller opened the preview" email to
// the travel agent. Its own tab rather than a card on Integrations, because it
// is a piece of org-configurable BEHAVIOUR (a trigger rule, a delay and an
// editable template) with a test send of its own, not a connection to an
// outside service.
export const systemConfigPreviewFollowUpRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'preview-follow-up',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-preview-follow-up').then((m) => ({
      default: m.SystemConfigPreviewFollowUpPage,
    }))
  ),
});

// Partner Configuration (Eleggia DMC profile) — a standalone top-level page,
// reachable from the admin dropdown (System Management → Partner Configuration).
// Moved out of the System Configuration tab surface; permission-gated by
// `dmc_profile:read` in `route-permissions.ts` so the 5 Eleggia DMC roles + admin
// qualify (not admin-only). The active module is a `?pane=<id>` search param.
export const partnerConfigurationRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'partner-configuration',
  // `tab` switches the two top-level views (Profile / Consortium Directory);
  // `pane` is the profile editor's active module within the Profile tab. Both
  // are deep-linkable with working browser back/forward.
  validateSearch: (
    search: Record<string, unknown>
  ): { tab?: string; pane?: string } => ({
    tab: typeof search.tab === 'string' ? search.tab : undefined,
    pane: typeof search.pane === 'string' ? search.pane : undefined,
  }),
  component: lazyRouteComponent(() =>
    import('@auth/pages/partner-configuration').then((m) => ({
      default: m.PartnerConfigurationPage,
    }))
  ),
});

export const systemConfigBudgetSettingsRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'budget-settings',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-budget-settings').then((m) => ({
      default: m.SystemConfigBudgetSettingsPage,
    }))
  ),
});

export const systemConfigPricingRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'pricing',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-pricing').then((m) => ({
      default: m.SystemConfigPricingPage,
    }))
  ),
});

// The operation status catalog. It spent XT-708 phase 3 on /status-flow and
// came back here in XT-773, when that page became super-user-only: which
// statuses an org has is its own administration, how they may move is not.
export const systemConfigOperationsStatusRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'operations-status',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-operations-status').then((m) => ({
      default: m.SystemConfigOperationsStatusPage,
    }))
  ),
});

export const systemConfigTravellerTypesRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'traveller-types',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-traveller-types').then((m) => ({
      default: m.SystemConfigTravellerTypesPage,
    }))
  ),
});

// Layout templates moved to the CRM tab row (`/crm/layouts`) — consultants own
// their agency's layouts but never render the admin menu that was this page's
// only entry point. Kept as a redirect so existing bookmarks and links resolve.
export const systemConfigLayoutTemplatesRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'layout-templates',
  beforeLoad: () => {
    throw redirect({ to: '/crm/layouts' });
  },
});

export const systemConfigTicketVenuesRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'ticket-venues',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-ticket-venues').then((m) => ({
      default: m.SystemConfigTicketVenuesPage,
    }))
  ),
});

export const systemConfigTicketLabelsRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'ticket-labels',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-ticket-labels').then((m) => ({
      default: m.SystemConfigTicketLabelsPage,
    }))
  ),
});

export const systemConfigVideoSuggestionsRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'video-suggestions',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-video-suggestions').then((m) => ({
      default: m.SystemConfigVideoSuggestionsPage,
    }))
  ),
});

export const systemConfigTermsAndConditionsRoute = createRoute({
  getParentRoute: () => systemConfigRoute,
  path: 'terms-and-conditions',
  component: lazyRouteComponent(() =>
    import('@auth/pages/system-config-terms-and-conditions').then((m) => ({
      default: m.SystemConfigTermsAndConditionsPage,
    }))
  ),
});

// ─── Pricing Configuration ───────────────────────────────────────

export const pricingConfigurationRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'pricing-configuration',
  component: Outlet,
});

export const pricingConfigurationIndexRoute = createRoute({
  getParentRoute: () => pricingConfigurationRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/pricing-configuration/pricing-type' });
  },
});

export const pricingConfigPricingTypeRoute = createRoute({
  getParentRoute: () => pricingConfigurationRoute,
  path: 'pricing-type',
  validateSearch: (input: Record<string, unknown>) =>
    validatePricingTypeSearch(input),
  component: lazyRouteComponent(() =>
    import('@products/pages/pricing-config-pricing-type').then((m) => ({
      default: m.PricingConfigPricingTypePage,
    }))
  ),
});

export const pricingConfigDefaultPricingRoute = createRoute({
  getParentRoute: () => pricingConfigurationRoute,
  path: 'default-pricing',
  component: lazyRouteComponent(() =>
    import('@auth/pages/pricing-config-default-pricing').then((m) => ({
      default: m.PricingConfigDefaultPricingPage,
    }))
  ),
});

export const pricingConfigVatRoute = createRoute({
  getParentRoute: () => pricingConfigurationRoute,
  path: 'vat',
  validateSearch: (input: Record<string, unknown>) => validateVatSearch(input),
  component: lazyRouteComponent(() =>
    import('@products/pages/pricing-config-vat').then((m) => ({
      default: m.PricingConfigVatPage,
    }))
  ),
});

export const pricingConfigExchangeRateRoute = createRoute({
  getParentRoute: () => pricingConfigurationRoute,
  path: 'exchange-rate',
  component: lazyRouteComponent(() =>
    import('@auth/pages/pricing-config-exchange-rate').then((m) => ({
      default: m.PricingConfigExchangeRatePage,
    }))
  ),
});

// ─── Export Configuration ────────────────────────────────────────

export const exportConfigurationRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'export-configuration',
  component: Outlet,
});

export const exportConfigurationIndexRoute = createRoute({
  getParentRoute: () => exportConfigurationRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/export-configuration/products' });
  },
});

export const exportConfigurationProductsRoute = createRoute({
  getParentRoute: () => exportConfigurationRoute,
  path: 'products',
  component: lazyRouteComponent(() =>
    import('@products/pages/export-configuration').then((m) => ({
      default: m.ExportConfigurationPage,
    }))
  ),
});

// ─── API Monitoring ──────────────────────────────────────────────

export const apiMonitoringRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'api-monitoring',
  component: Outlet,
});

export const apiMonitoringIndexRoute = createRoute({
  getParentRoute: () => apiMonitoringRoute,
  path: '/',
  beforeLoad: () => {
    // biome-ignore lint/suspicious/noExplicitAny: cross-domain route reference
    throw redirect({ to: '/api-monitoring/monitor' as any });
  },
});

export const apiMonitoringMonitorRoute = createRoute({
  getParentRoute: () => apiMonitoringRoute,
  path: 'monitor',
  component: lazyRouteComponent(() =>
    import('@api-monitoring/pages/api-monitoring-monitor').then((m) => ({
      default: m.ApiMonitoringMonitorPage,
    }))
  ),
  validateSearch: validateMonitorSearch,
});

export const apiMonitoringManualRoute = createRoute({
  getParentRoute: () => apiMonitoringRoute,
  path: 'manual-request',
  component: lazyRouteComponent(() =>
    import('@api-monitoring/pages/api-monitoring-manual').then((m) => ({
      default: m.ApiMonitoringManualPage,
    }))
  ),
});

export const inconsistencyResolverRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'inconsistency-resolver',
  component: lazyRouteComponent(() =>
    import('@api-monitoring/pages/inconsistency-resolver').then((m) => ({
      default: m.InconsistencyResolverPage,
    }))
  ),
});
